import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from 'app/core/auth/auth.service';
import { catchError, Observable, switchMap, throwError } from 'rxjs';

const isAuthRoute = (req: HttpRequest<unknown>): boolean =>
    req.url.includes('/api/auth/');

const withBearer = (
    req: HttpRequest<unknown>,
    token: string,
): HttpRequest<unknown> =>
    req.clone({ headers: req.headers.set('Authorization', 'Bearer ' + token) });

/**
 * Intercept
 *
 * - Ajoute le jeton d’accès aux appels API.
 * - Si le jeton est expiré, le renouvelle d’abord via le cookie de
 *   rafraîchissement (un seul appel partagé pour les requêtes concurrentes).
 * - Sur `401`, tente un renouvellement puis rejoue la requête une fois ;
 *   en cas d’échec, déconnecte.
 */
export const authInterceptor = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);

    // Les routes d’authentification gèrent elles-mêmes leur session.
    if (isAuthRoute(req)) {
        return next(req);
    }

    const send = (): Observable<HttpEvent<unknown>> =>
        next(
            authService.accessToken
                ? withBearer(req, authService.accessToken)
                : req,
        );

    /**
     * Un renouvellement qui échoue ne vaut pas déconnexion. `signOut` révoque
     * la session côté API et vide le cache : le faire parce que le serveur
     * n'a pas répondu coûterait une reconnexion complète, code par mail
     * compris, pour une coupure de quelques secondes. Seul un refus explicite
     * de l'API y mène ; le reste échoue la requête et laisse la session en
     * place, prête à repartir au retour du serveur.
     */
    const refreshThenSend = (): Observable<HttpEvent<unknown>> =>
        authService.refreshSession().pipe(
            switchMap((outcome) => {
                if (outcome === 'refreshed') {
                    return send();
                }

                const failure = new HttpErrorResponse({
                    status: 401,
                    statusText: 'Unauthorized',
                    url: req.url,
                });

                if (outcome === 'unreachable') {
                    return throwError(() => failure);
                }

                return authService
                    .signOut()
                    .pipe(switchMap(() => throwError(() => failure)));
            }),
        );

    /**
     * Une session restaurée hors ligne rétablit l’identité sans jeton d’accès :
     * le cookie de rafraîchissement, lui, est toujours là. Se fier au seul
     * `accessToken` laisserait ces sessions enchaîner les `401` au retour du
     * réseau sans jamais tenter le renouvellement qui les sauverait.
     */
    const canRefresh = (): boolean =>
        !!authService.accessToken || !!authService.user();

    // Jeton absent ou expiré alors qu’une identité est connue : renouveler
    // avant d’envoyer.
    if (canRefresh() && !authService.hasValidAccessToken) {
        return refreshThenSend();
    }

    return send().pipe(
        catchError((error) => {
            if (
                error instanceof HttpErrorResponse &&
                error.status === 401 &&
                canRefresh()
            ) {
                // Jeton refusé (révoqué, expiré côté serveur) : une seule tentative.
                return authService.refreshSession().pipe(
                    switchMap((outcome) => {
                        if (outcome === 'refreshed') {
                            return send();
                        }

                        // Même règle qu'au-dessus : on ne déconnecte que sur
                        // un refus de l'API, jamais sur son silence.
                        if (outcome === 'unreachable') {
                            return throwError(() => error);
                        }

                        return authService
                            .signOut()
                            .pipe(switchMap(() => throwError(() => error)));
                    }),
                );
            }

            return throwError(() => error);
        }),
    );
};
